/* eslint-disable no-nested-ternary */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Avatar, ImageIcon } from '@saleshandy/design-system';
import {
  Check,
  FacebookBw,
  LinkedinBw,
  Link as LinkIcon,
  Plus,
} from '@saleshandy/icons';
import Button from '../../../../../../shared/design-system/components/atoms/button';
import { CallLog, DialerCallSourceEnum } from '../../../../types/call-logs';
import './call-logs-prospect-drawer-header.scss';
import CreateStepModal from '../../../../../sequence/shared/modals/create-step-modal';
import { SequenceStepType, StepType } from '../../../../../sequence/enums';
import { CallLogsDrawerOutgoingCall } from '../../../../../../shared/svg/call-logs-drawer-outgoing-call';
import { CallLogsDrawerIncomingCall } from '../../../../../../shared/svg/call-logs-drawer-incoming-call';
import { CallLogsDrawerMissedCall } from '../../../../../../shared/svg/call-logs-drawer-missed-call';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  formatNoteTimestamp,
  getIsRequestPending,
} from '../../../../../../shared/utils';
import { formatDuration } from '../../../../../inbox-radar/components/test-report/components/test-report-content/components/sender-email-accounts/utils/helper';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
  TriggerType,
} from '../../../../../../shared/design-system/components/overlay';
import AddProspectManuallyModal from '../../../../../prospect/components/prospect-list/components/modals/add-prospect-manually-modal';
import LinkNumberToProspectModal from '../../../../../../shared/components/dialer/components/post-call-outcome-modal/link-number-to-prospect-modal';
import UpgradePlanErrorModal from '../../../../../../shared/components/upgrade-plan-error-modal';
import RestrictionErrorModal from '../../../../../prospect/components/prospect-list/components/modals/restriction-error-modal';
import {
  getRestrictionPlanModalBodyContent,
  getRestrictionPlanWarningEmailBody,
} from '../../../../../prospect/components/prospect-list/utils/helper';
import hasResource from '../../../../../../shared/utils/access-control/has-resource';
import { ResourceIdentifiers } from '../../../../../../shared/utils/access-control/enums/resource-identifiers';
import planPermissionError from '../../../../../../shared/utils/errors/plan-permission-error';
import { isFreePlan } from '../../../../../../shared/utils/plans';
import { starterPlanRestriction } from '../../../../../../shared/utils/plan-restriction';
import { NogenderAvatar } from '../../../../../../shared/svg/no-gender-avatar';
import TruncatedTextWithAutoTooltip from '../../../../../../shared/components/truncated-text-with-auto-tooltip';
import { getValidUrl } from '../../../../../prospect/components/prospect-list/components/prospect-details/prospect-details-modal/helpers/prospect-attributes';
import { generateProspectDetailsUrl } from '../../../../../../shared/utils/prospect-details-url';
import TwitterIcon from '../../../../../../shared/svg/twitter-icon';
import { CallLogsProspectDrawerHeaderSkeleton } from '.';
import { ViewActivity } from '../../../../../../shared/svg/view-activity';
import CallLogsSingleCallDetail from '../call-logs-missed-call-detail/call-logs-single-call-detail-container';
import { setSelectedCallbackProspect } from '../../../../dialer-slice';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import type { CallLogsProspectDrawerHeaderReduxProps } from './call-logs-prospect-drawer-header-redux';
import CallIncoming from '../../../../../../shared/components/images/CallIncoming';
import { PhoneSlash } from '../../../../../../shared/svg/phone-slash';
import { isOngoingDialerCall } from '../../../../../../shared/components/dialer/constants';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <polyline
      points="6 9 12 15 18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type CallLogsProspectDrawerHeaderProps = CallLogsProspectDrawerHeaderReduxProps & {
  row: CallLog;
  selectedProspectId?: number | null;
  /** Fired when user chooses "Do Not Contact" from the header overflow menu */
  onDoNotContactClick?: () => void;
  /** Fired when user clicks "View User Activity" (e.g. switch prospect modal to Activities tab) */
  onViewActivityClick?: () => void;
  isDemoAccount?: boolean;
};

/**
 * Top section for the prospect drawer when opened from Call Logs.
 * Replace or extend this layout without changing core ProspectDetailsModal logic.
 */
const CallLogsProspectDrawerHeader: React.FC<CallLogsProspectDrawerHeaderProps> = ({
  row,
  selectedProspectId,
  userTimezone,
  getCallLogsProspectProfileSectionResponse,
  getCallLogsProspectProfileSectionRequestStatus,
  resetGetCallLogsProspectProfileSectionRequest,
  onDoNotContactClick,
  onViewActivityClick,
  createTaskRequestStatus,
  sendCreateTaskRequest,
  resetCreateTaskRequest,
  remainingProspectLimit,
  isDemoAccount,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentCallManagementState = useSelector(
    (state: any) => state?.dialer?.currentCallManagementState,
  );
  const isOngoingCall = isOngoingDialerCall({
    callIsInprogress: currentCallManagementState?.callIsInprogress,
    callStatus: currentCallManagementState?.callStatus,
  });
  const [
    isCreateTaskStepModalVisible,
    setIsCreateTaskStepModalVisible,
  ] = useState(false);
  const [stepType, setStepType] = useState(StepType.Call);
  const [initialStepType, setInitialStepType] = useState(SequenceStepType.Call);
  const [copiedCallLinkId, setCopiedCallLinkId] = useState<string | null>(null);
  const [showLinkNumberMenu, setShowLinkNumberMenu] = useState(false);
  const [
    showAddProspectManuallyModal,
    setShowAddProspectManuallyModal,
  ] = useState(false);
  const [
    showLinkNumberToProspectModal,
    setShowLinkNumberToProspectModal,
  ] = useState(false);
  const [showUpgradePlanErrorModal, setShowUpgradePlanErrorModal] = useState(
    false,
  );
  const [isRestrictionModalVisible, setIsRestrictionModalVisible] = useState(
    false,
  );
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const callDirectionOutgoing =
    getCallLogsProspectProfileSectionResponse?.type === 'outbound';

  const callDirectionIncoming =
    getCallLogsProspectProfileSectionResponse?.type === 'inbound' &&
    getCallLogsProspectProfileSectionResponse?.isAnswered === true;

  const callLogsProspectProfileSection = getCallLogsProspectProfileSectionResponse;
  const prospectName = callLogsProspectProfileSection?.prospect;
  const jobTitle = prospectName?.jobTitle;
  const company = prospectName?.company;
  const isNumberInDnc = Boolean(callLogsProspectProfileSection?.isNumberInDnc);

  const isAccessible = row?.isProspectAccessible !== false;

  const prospectId = useMemo(() => {
    if (!isAccessible) return undefined;
    const fromRow = row?.prospect?.id;
    const fromProfile = callLogsProspectProfileSection?.prospect?.id;
    return fromRow ?? fromProfile ?? undefined;
  }, [
    row?.prospect?.id,
    callLogsProspectProfileSection?.prospect?.id,
    isAccessible,
  ]);

  const hasCallbackNumber = useMemo(
    () =>
      Boolean(
        row?.prospectPhone?.number?.trim() ||
          row?.prospectPhone?.phoneNumber?.trim(),
      ),
    [row?.prospectPhone?.number, row?.prospectPhone?.phoneNumber],
  );

  const callerPhone = useMemo(
    () =>
      row?.prospectPhone?.number?.trim() ||
      row?.prospectPhone?.phoneNumber?.trim() ||
      '',
    [row?.prospectPhone?.phoneNumber, row?.prospectPhone?.number],
  );

  const planUpgradeError = useCallback(() => {
    const title = 'Please upgrade your plan to start adding prospects';
    const content = [
      'Your account is downgraded to a free plan. Upgrade to paid plan to add prospects and resume email sending.',
    ];
    planPermissionError.showModal(title, content, true);
  }, []);

  const handleShowRestrictionErrorModal = useCallback(
    () => setIsRestrictionModalVisible(true),
    [],
  );

  const onAddProspectsButtonClick = useCallback(() => {
    if (isFreePlan()) {
      planUpgradeError();
      return;
    }
    if (starterPlanRestriction()) {
      setShowUpgradePlanErrorModal(true);
      return;
    }
    if (parseInt(String(remainingProspectLimit ?? '0'), 10) === 0) {
      handleShowRestrictionErrorModal();
      return;
    }
    if (hasResource(ResourceIdentifiers.CONTACTS_IMPORT)) {
      setShowAddProspectManuallyModal(true);
    }
  }, [
    planUpgradeError,
    remainingProspectLimit,
    handleShowRestrictionErrorModal,
  ]);

  const handleFooterCallBack = useCallback(() => {
    if (!hasCallbackNumber) return;

    dispatch(
      setSelectedCallbackProspect([
        {
          callSource: DialerCallSourceEnum.Manual,
          prospect: {
            id: row?.prospect?.id,
            firstName: row?.prospect?.firstName,
            lastName: row?.prospect?.lastName,
          },
          prospectPhone: {
            callingCode: row?.prospectPhone?.callingCode,
            number: row?.prospectPhone?.number,
            phoneNumber: row?.prospectPhone?.phoneNumber,
          },
        } as CallLog,
      ]),
    );
  }, [dispatch, hasCallbackNumber, row]);

  const callRowIdForLink = useMemo(() => {
    if (row?.id != null) return String(row.id);
    if (row?.callId != null && String(row.callId).trim() !== '') {
      return String(row.callId);
    }
    return '';
  }, [row?.id, row?.callId]);

  const handleCopyCallLink = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (
        row?.id == null ||
        !callRowIdForLink ||
        typeof navigator === 'undefined' ||
        !navigator?.clipboard?.writeText
      ) {
        return;
      }

      const origin = window?.location?.origin ?? '';
      const path = generateProspectDetailsUrl(
        location.pathname,
        row?.id,
        'calls',
      );
      const callLink = `${origin}${path}?callId=${encodeURIComponent(
        callRowIdForLink,
      )}`;

      try {
        await navigator.clipboard.writeText(callLink);
        setCopiedCallLinkId(callRowIdForLink);
        setTimeout(() => {
          setCopiedCallLinkId((prev) =>
            prev === callRowIdForLink ? null : prev,
          );
        }, 2000);
      } catch {
        // Clipboard may be denied; avoid console noise in production UI
      }
    },
    [callRowIdForLink, location.pathname, row?.id],
  );

  const onStepTypeChange = useCallback((label: StepType) => {
    switch (label) {
      case StepType.WriteWithAi:
        setStepType(StepType.WriteWithAi);
        setInitialStepType(SequenceStepType.WriteWithAi);
        break;
      case StepType.LinkedIn:
        setStepType(StepType.LinkedIn);
        setInitialStepType(SequenceStepType.LinkedInConnectionRequest);
        break;
      case StepType.Call:
        setStepType(StepType.Call);
        setInitialStepType(SequenceStepType.Call);
        break;
      case StepType.WhatsApp:
        setStepType(StepType.WhatsApp);
        setInitialStepType(SequenceStepType.WhatsAppMessage);
        break;
      case StepType.Task:
        setStepType(StepType.Task);
        setInitialStepType(SequenceStepType.Custom);
        break;
      default:
        setStepType(StepType.LinkedIn);
        setInitialStepType(SequenceStepType.LinkedInConnectionRequest);
    }
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: getCallLogsProspectProfileSectionRequestStatus,
      onSuccess: () => {
        resetGetCallLogsProspectProfileSectionRequest?.();
      },
      onFailed: () => {
        resetGetCallLogsProspectProfileSectionRequest?.();
      },
    });
  }, [getCallLogsProspectProfileSectionRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: createTaskRequestStatus,
      onSuccess: () => {
        setIsCreateTaskStepModalVisible(false);
        resetCreateTaskRequest();
      },
      onFailed: () => {
        resetCreateTaskRequest();
      },
    });
  }, [createTaskRequestStatus, resetCreateTaskRequest]);
  if (getIsRequestPending(getCallLogsProspectProfileSectionRequestStatus)) {
    return <CallLogsProspectDrawerHeaderSkeleton />;
  }

  return (
    <div className="bs-call-logs-prospect-drawer-header">
      {/* Header section */}
      <div className="bs-call-logs-prospect-drawer-header__header-section-container">
        <div className="bs-call-logs-prospect-drawer-header__header-section">
          {callDirectionOutgoing ? (
            <span className="bs-call-logs-prospect-drawer-header__icon-outgoing">
              <CallLogsDrawerOutgoingCall width={20} height={20} />
            </span>
          ) : callDirectionIncoming ? (
            <span className="bs-call-logs-prospect-drawer-header__icon-incoming">
              <CallLogsDrawerIncomingCall width={20} height={20} />
            </span>
          ) : (
            <span className="bs-call-logs-prospect-drawer-header__icon-missed">
              <CallLogsDrawerMissedCall width={20} height={20} />
            </span>
          )}
          <div className="bs-call-logs-prospect-drawer-header__header-section__text-section">
            <span className="bs-call-logs-prospect-drawer-header__header-section__text-section__call-direction-text">
              <span>
                {callDirectionOutgoing
                  ? 'Outgoing Call'
                  : callDirectionIncoming
                  ? 'Incoming Call'
                  : 'Missed Call'}
              </span>
              <button
                type="button"
                className="bs-call-logs-prospect-drawer-header-icon-link"
                disabled={!callRowIdForLink}
                {...accessibleOnClick(handleCopyCallLink)}
                aria-label={
                  copiedCallLinkId === callRowIdForLink
                    ? 'Call link copied'
                    : 'Copy call link'
                }
              >
                {copiedCallLinkId === callRowIdForLink ? (
                  <Check height={14} width={14} />
                ) : (
                  <LinkIcon height={14} width={14} />
                )}
              </button>
            </span>
            <span className="bs-call-logs-prospect-drawer-header__header-section__text-section__date-time-text">
              {formatNoteTimestamp(
                callLogsProspectProfileSection?.callStartedAt,
                false,
                userTimezone,
                true,
              )}
              {(callDirectionOutgoing || callDirectionIncoming) &&
                callLogsProspectProfileSection?.callDuration > 0 && (
                  <div className="bs-call-logs-prospect-drawer-header__header-section__text-section__call-duration">
                    <OverlayTooltip text="Talktime">
                      <span>
                        {formatDuration(
                          callLogsProspectProfileSection?.callDuration,
                        )}
                      </span>
                    </OverlayTooltip>
                  </div>
                )}
            </span>
          </div>
        </div>
        <div className="bs-call-logs-prospect-drawer-header__prospect-profile-section">
          <div
            className={`prospect-profile-section-header-container ${
              prospectName?.jobTitle && prospectName?.company
                ? ''
                : 'prospect-profile-no-job-title-company'
            }`}
          >
            <div className="prospect-profile-section-header">
              {prospectName?.profilePicture ? (
                <ImageIcon
                  src={prospectName?.profilePicture}
                  alt="profile-picture"
                  width={64}
                  height={64}
                  className="prospect-profile-picture"
                />
              ) : prospectName?.firstName || prospectName?.lastName ? (
                <Avatar
                  firstName={prospectName?.firstName || 'N'}
                  lastName={prospectName?.lastName || 'N'}
                  size="lg"
                  className="cs-avatar"
                />
              ) : (
                <div className="avatar-icon cs-avatar">
                  <NogenderAvatar width={64} height={64} />
                </div>
              )}
              <div className="prospect-profile-section-header-user-detail">
                <div className="prospect-profile-section-header-user-detail-icon-name">
                  <div className="prospect-profile-section-header-user-name">
                    {prospectName?.firstName || prospectName?.lastName ? (
                      <TruncatedTextWithAutoTooltip
                        text={`${prospectName?.firstName ?? ''} ${
                          prospectName?.lastName ?? ''
                        }`.trim()}
                        className="prospect-name-call-logs gray-txt-10"
                      />
                    ) : (
                      <span className="no-name-text">(No Name)</span>
                    )}
                    {isNumberInDnc && (
                      <div className="bs-call-logs-prospect-drawer-header__dnc-chip">
                        Do Not Call
                      </div>
                    )}
                  </div>
                  <span className="prospect-profile-section-header-user-designation">
                    {(() => {
                      const website = prospectName?.website;
                      if (jobTitle && company) {
                        return (
                          <div className="bs-call-logs-prospect-drawer-header__job-company-line">
                            <div className="bs-call-logs-prospect-drawer-header__job-title-col job-title-prospect">
                              <div className="bs-call-logs-prospect-drawer-header__job-title-ellipsis-wrap">
                                <TruncatedTextWithAutoTooltip
                                  text={jobTitle}
                                  className="prospect-job-title"
                                />
                              </div>
                              <span className="bs-call-logs-prospect-drawer-header__job-at">
                                at
                              </span>
                            </div>
                            <div className="bs-call-logs-prospect-drawer-header__company-col">
                              {website || getValidUrl(website) ? (
                                <a
                                  href={getValidUrl(website)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bs-call-logs-prospect-drawer-header__company-link"
                                >
                                  <TruncatedTextWithAutoTooltip
                                    text={company}
                                    className={`prospect-company-name ${
                                      website ? 'company-name-website' : ''
                                    }`}
                                  />
                                </a>
                              ) : (
                                <TruncatedTextWithAutoTooltip
                                  text={company}
                                  className="prospect-company-name"
                                />
                              )}
                            </div>
                          </div>
                        );
                      }
                      if (jobTitle) {
                        return (
                          <div className="bs-call-logs-prospect-drawer-header__job-title-only">
                            <TruncatedTextWithAutoTooltip
                              text={jobTitle}
                              className="prospect-job-title"
                            />
                          </div>
                        );
                      }

                      if (company) {
                        return (
                          <div className="d-flex align-items-center gap-4 w-100">
                            {website || getValidUrl(website) ? (
                              <a
                                href={getValidUrl(website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-flex w-100"
                              >
                                <TruncatedTextWithAutoTooltip
                                  text={company}
                                  className={`prospect-company-name ${
                                    website ? 'company-name-website' : ''
                                  }`}
                                />
                              </a>
                            ) : (
                              <TruncatedTextWithAutoTooltip
                                text={company}
                                className="prospect-company-name"
                              />
                            )}
                          </div>
                        );
                      }

                      return '';
                    })()}
                  </span>
                </div>
                <div className="prospect-profile-section-header-user-social-media">
                  {prospectName?.facebook && (
                    <a
                      href={getValidUrl(prospectName?.facebook)}
                      key={prospectName?.id}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="facebook-icon"
                    >
                      <FacebookBw
                        className="facebook-icon"
                        width={20}
                        height={20}
                      />
                    </a>
                  )}

                  {prospectName?.linkedin && (
                    <a
                      href={getValidUrl(prospectName?.linkedin)}
                      key={prospectName?.id}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="linkedin-icon"
                    >
                      <LinkedinBw
                        className="linkedin-icon"
                        width={20}
                        height={20}
                      />
                    </a>
                  )}
                  {prospectName?.twitter && (
                    <a
                      href={getValidUrl(prospectName?.twitter)}
                      key={prospectName?.id}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="twitter-icon"
                    >
                      <TwitterIcon
                        className="twitter-icon"
                        width={20}
                        height={20}
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="prospect-profile-section-header-user-activity">
              {selectedProspectId && (
                <OverlayTooltip text="View User Activity">
                  <div
                    className="prospect-profile-section-header-user-activity-view-activity"
                    {...(onViewActivityClick
                      ? accessibleOnClick(onViewActivityClick)
                      : {})}
                  >
                    <ViewActivity width={16} height={16} />
                  </div>
                </OverlayTooltip>
              )}
              {!isNumberInDnc && (
                <OverlayTooltip text="Do Not Call">
                  <div
                    className="bs-call-logs-prospect-drawer-header__dnc-btn"
                    {...accessibleOnClick(() => {
                      if (isDemoAccount) {
                        setShowGlobalRestrictionModal(true);
                        return;
                      }
                      onDoNotContactClick?.();
                    })}
                  >
                    <PhoneSlash width={16} height={16} />
                  </div>
                </OverlayTooltip>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bs-call-logs-prospect-drawer-header__missed-call-detail">
        <CallLogsSingleCallDetail
          row={row}
          callLogsProspectProfileSection={callLogsProspectProfileSection}
        />
      </div>

      <div className="bs-call-logs-prospect-drawer-header__footer-container">
        <div className="bs-call-logs-prospect-drawer-header__footer">
          {!prospectId ? (
            <div
              className={`bs-call-logs-prospect-drawer-header__split-btn${
                showLinkNumberMenu ? ' show-link-number-menu' : ''
              }`}
            >
              <OverlayTooltip
                text="Create a new prospect using this number."
                placement={Placement.Top}
              >
                <Button
                  variant={Button.Variant.Outlined}
                  className="bs-call-logs-prospect-drawer-header__split-main"
                  onClick={onAddProspectsButtonClick}
                >
                  Add as Prospect
                </Button>
              </OverlayTooltip>
              <OverlayPopover
                show={showLinkNumberMenu}
                onToggle={setShowLinkNumberMenu}
                placement={Placement.TopEnd}
                trigger={TriggerType.Click}
                rootClose
                className="bs-call-logs-prospect-drawer-header__link-number-popover p-0"
                content={
                  <div className="bs-call-logs-prospect-drawer-header__link-number-popover-inner">
                    <button
                      type="button"
                      className="bs-call-logs-prospect-drawer-header__link-number-row regular-2 gray-txt-9"
                      {...accessibleOnClick(() => {
                        setShowLinkNumberMenu(false);
                        setShowLinkNumberToProspectModal(true);
                      })}
                    >
                      Link number to existing prospect
                    </button>
                  </div>
                }
              >
                <Button
                  variant={Button.Variant.Outlined}
                  className="bs-call-logs-prospect-drawer-header__split-chevron"
                  aria-expanded={showLinkNumberMenu}
                  aria-haspopup="true"
                  aria-label="More add prospect options"
                >
                  <ChevronDownIcon />
                </Button>
              </OverlayPopover>
            </div>
          ) : (
            <Button
              className="call-logs-footer-btn"
              {...accessibleOnClick(() => {
                setStepType(StepType.Call);
                setInitialStepType(SequenceStepType.Call);
                setIsCreateTaskStepModalVisible(true);
              })}
            >
              <Plus width={16} height={16} color="#1d4ed8" />
              <span className="call-logs-footer-btn-text">
                Add Follow-up Task
              </span>
            </Button>
          )}
          {hasPermission(Permissions.PHONE_CALL_WRITE) &&
            (hasCallbackNumber ? (
              isOngoingCall ? (
                <OverlayTooltip
                  text="Another call is already in progress"
                  placement={Placement.Top}
                  className="cell-card__tooltip"
                >
                  <Button
                    variant={Button.Variant.Primary}
                    theme={Button.Theme.Solid}
                    className="ml-8 prospect-drawer-header-footer-call-btn"
                    disabled
                  >
                    Call
                  </Button>
                </OverlayTooltip>
              ) : (
                <Button
                  variant={Button.Variant.Primary}
                  theme={Button.Theme.Solid}
                  className="ml-8 prospect-drawer-header-footer-call-btn"
                  {...accessibleOnClick(() => handleFooterCallBack())}
                >
                  Call
                </Button>
              )
            ) : (
              <Button
                variant={Button.Variant.Primary}
                theme={Button.Theme.Solid}
                className="ml-8 prospect-drawer-header-footer-call-btn"
                disabled
              >
                Call
              </Button>
            ))}
        </div>
      </div>

      {prospectId && isCreateTaskStepModalVisible ? (
        <div {...accessibleOnClick((e) => e.stopPropagation())}>
          <CreateStepModal
            show={isCreateTaskStepModalVisible}
            stepType={stepType}
            initialStepType={initialStepType}
            onClose={() => setIsCreateTaskStepModalVisible(false)}
            subsequenceId={undefined}
            onStepTypeChange={onStepTypeChange}
            excludeStepTypes={[StepType.Email]}
            isTaskModal={true}
            sendCreateTaskRequest={sendCreateTaskRequest}
            prospectId={prospectId}
            className="create-task-modal"
            showCloseIcon={false}
          />
        </div>
      ) : null}

      {showAddProspectManuallyModal && (
        <AddProspectManuallyModal
          show={showAddProspectManuallyModal}
          onClose={() => setShowAddProspectManuallyModal(false)}
          callId={row?.id}
          callerPhone={callerPhone}
        />
      )}

      {showLinkNumberToProspectModal && (
        <LinkNumberToProspectModal
          show={showLinkNumberToProspectModal}
          onClose={() => setShowLinkNumberToProspectModal(false)}
          callerPhone={callerPhone}
          callId={row?.id}
          onSubmit={() => {}}
        />
      )}

      {showUpgradePlanErrorModal && (
        <UpgradePlanErrorModal
          show={showUpgradePlanErrorModal}
          onClose={() => setShowUpgradePlanErrorModal(false)}
          planName="starter"
          restrictionOn="prospect"
        />
      )}

      {isRestrictionModalVisible && (
        <RestrictionErrorModal
          show={isRestrictionModalVisible}
          onClose={() => setIsRestrictionModalVisible(false)}
          bodyContent={getRestrictionPlanModalBodyContent()}
          emailBody={getRestrictionPlanWarningEmailBody()}
        />
      )}
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Adding to DNC list is disabled in demo mode"
        content="This is a demo account with sample data. Signup to add to DNC list"
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

export default memo(CallLogsProspectDrawerHeader);
